/**
 * Phase 22 — the volume floors as a rule (user, 12/10/2026: "follow the Flow
 * Optimizer… clean and fully understandable everywhere"): every surface's
 * default rule is "Not enough data — Unique visits < N over the formula's
 * first range → Exclude". Here: that rule, the old stored shapes converted
 * to it (a surface's floor; a rule's own `minVolume` becomes a visible first
 * condition "Unique visits ≥ N"), and the first-range marker resolved
 * against a formula before anything is judged or fetched.
 * For Media Devoted | Created By Ciro Autiero
 */
import { DROP_GEN_EXCLUDE_ACTION, DROP_GEN_FIRST_RANGE, DROP_GEN_RULE_LIMITS, type DropGenRule, type DropGenRuleNode } from "./rules.types";

export const NOT_ENOUGH_DATA_ID = "not-enough-data";

/** The default rule: fewer than `minVisits` unique visits over the formula's first range → exclude. */
export function notEnoughDataRule<A extends string>(minVisits: number): DropGenRule<A> {
  return {
    id: NOT_ENOUGH_DATA_ID,
    name: "Not enough data",
    enabled: true,
    dateRange: DROP_GEN_FIRST_RANGE,
    when: { kind: "group", op: "AND", children: [{ kind: "condition", metric: "uniqueVisits", op: "<", value: minVisits }] },
    action: DROP_GEN_EXCLUDE_ACTION as A,
  };
}

/** A stored surface floor (old shape) → its default rule first in the list; 0 = no rule. */
export function withFloorRule(rules: unknown, floor: unknown): unknown[] {
  const list = Array.isArray(rules) ? rules : [];
  if (typeof floor !== "number" || floor <= 0) return list;
  if (list.some((r) => (r as { id?: unknown })?.id === NOT_ENOUGH_DATA_ID)) return list;
  return [notEnoughDataRule(floor), ...list];
}

/** A surface's stored config → its rules: an OLD config (it still has its floor key, or no rules list yet)
 *  gets the floor as the default rule first; a current one is read as stored. */
export function rulesWithLegacyFloor(stored: Record<string, unknown>, floorKey: string, defaultFloor: number): unknown[] {
  const legacy = floorKey in stored || !Array.isArray(stored.rules);
  if (!legacy) return stored.rules as unknown[];
  const floor = stored[floorKey];
  return withFloorRule(stored.rules, typeof floor === "number" && Number.isFinite(floor) ? Math.max(0, Math.round(floor)) : defaultFloor);
}

/** A rule's own old `minVolume` → the visible condition "Unique visits ≥ N" (same outcome: under it, it never acts). */
export function floorAsCondition(rule: Record<string, unknown>): Record<string, unknown> {
  if (!("minVolume" in rule)) return rule;
  const { minVolume, ...rest } = rule;
  if (typeof minVolume !== "number" || minVolume <= 0 || !rest.when) return rest;
  const floor: DropGenRuleNode = { kind: "condition", metric: "uniqueVisits", op: ">=", value: minVolume };
  const when = rest.when as DropGenRuleNode;
  const flat = when.kind === "group" && when.op === "AND" && when.children.length < DROP_GEN_RULE_LIMITS.conditions;
  return { ...rest, when: flat ? { ...when, children: [floor, ...when.children] } : { kind: "group", op: "AND", children: [floor, when] } };
}

type RangeFields = { dateRange: string; dateRangeCustomFrom?: string; dateRangeCustomTo?: string };

/** "first-range" rules read the formula's first range — resolved before judging or fetching. */
export function resolveFirstRange<R extends RangeFields>(rules: R[] | undefined, first: RangeFields | undefined): R[] {
  if (!rules) return [];
  if (!first) return rules;
  return rules.map((r) =>
    r.dateRange === DROP_GEN_FIRST_RANGE
      ? { ...r, dateRange: first.dateRange, dateRangeCustomFrom: first.dateRangeCustomFrom, dateRangeCustomTo: first.dateRangeCustomTo }
      : r,
  );
}
